import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { AuthenticationService } from '../authentication/services/authentication.service';
import { UserService } from '../authentication/services/user.service';
import { Subscription } from 'rxjs';
import { IDeactivateComponent } from '../authentication/i-deactivate/i-deactivate.component';
import { ProfilePageComponent } from './components/profile-page/profile-page.component';
import { TranslateService } from '@ngx-translate/core';
import { UserAuth } from '../authentication/profile/models/user-profile';

@Component({
    selector: 'app-user-area',
    templateUrl: './user-area.component.html',
    styleUrls: ['./user-area.component.scss'],
    standalone: false,
})
export class UserAreaComponent
    implements OnInit, OnDestroy, IDeactivateComponent
{
    @ViewChild(ProfilePageComponent) profilePageComponent: ProfilePageComponent;

    tabIndex: number = 0;
    userId: string;
    isUserExperimental: boolean = false;
    isUserApiTokenCreator: boolean = false;
    isOrgBoundUser: boolean = false;
    activeOrganization: string;
    profilePicture: string;
    largeHeadline: string;
    smallHeadlineTranslationKey: string;
    smallHeadlineIconPath: string;
    smallHeadlineRaw: string;
    userEmail: string;
    pictureSubscription: Subscription;

    constructor(
        private authenticationService: AuthenticationService,
        private userService: UserService,
        private translateService: TranslateService
    ) {}

    ngOnInit(): void {
        this.authenticationService
            .observableUserAuth()
            .subscribe((userAuth: UserAuth) => {
                this.userId = userAuth.id;
                this.activeOrganization = userAuth.activeOrganization;

                this.isOrgBoundUser =
                    this.authenticationService.isUserBoundToAnyOrg();
                this.isUserExperimental =
                    this.authenticationService.isExperimental();
                this.isUserApiTokenCreator =
                    this.authenticationService.isUserApiTokenCreator();
            });
        this.largeHeadline =
            this.authenticationService.currentUserValue.username;

        // Get user email for profile tab display
        this.userService.getUser(this.userId).subscribe((user) => {
            this.userEmail = user.email;
            this.updateHeadlineDisplay();
        });

        this.updateHeadlineDisplay();
        this.pictureSubscription = this.userService.profilePicture$.subscribe(
            (picture) => {
                this.profilePicture = picture
                    ? picture
                    : 'assets/svg/profile.svg';
            }
        );
    }
    getTabDescription(): string {
        switch (this.tabIndex) {
            case 0:
                return 'user.userArea.header.description.profile';
            case 1:
                return 'user.userArea.header.description.subscription';
            case 2:
                return 'user.userArea.header.description.apiManagement';
            case 5:
                return 'user.userArea.header.description.credits';
            default:
                return '';
        }
    }

    /**
     * Updates the headline display based on current tab.
     * For profile tab (0), shows email; for other tabs, shows translation key.
     */
    updateHeadlineDisplay(): void {
        if (this.tabIndex === 0 && this.userEmail) {
            this.smallHeadlineRaw = this.userEmail;
            this.smallHeadlineIconPath = 'assets/svg/reusable-svg/email.svg';
            this.smallHeadlineTranslationKey = null;
        } else {
            this.smallHeadlineRaw = null;
            this.smallHeadlineIconPath = null;
            this.smallHeadlineTranslationKey = this.getTabDescription();
        }
    }

    onSelectTab(tabIndex: number): void {
        // Check for unsaved changes before switching away from profile tab
        if (
            this.tabIndex === 0 &&
            this.profilePageComponent?.hasUnsavedChanges()
        ) {
            const confirmSwitch = window.confirm(
                this.translateService.instant(
                    'user.userArea.profile.unsavedChangesWarning'
                )
            );
            if (!confirmSwitch) {
                return; // Don't switch tabs
            }
        }
        this.tabIndex = tabIndex;
        this.updateHeadlineDisplay();
    }

    /**
     * Handles profile picture upload from area-header component.
     */
    onPictureSelected(file: File): void {
        this.userService.updateProfilePicture(file, this.userId).subscribe({
            next: () => {
                this.userService.getProfilePicture(this.userId);
            },
            error: (error) => {
                console.error('Profile picture could not be saved:', error);
            },
        });
    }

    ngOnDestroy(): void {
        if (this.pictureSubscription && !this.pictureSubscription.closed) {
            this.pictureSubscription.unsubscribe();
        }
    }

    /**
     * Implementation of IDeactivateComponent interface.
     * Delegates to ProfilePageComponent if it exists and tab is active.
     */
    canExit(): boolean {
        if (this.tabIndex === 0 && this.profilePageComponent) {
            return this.profilePageComponent.canExit();
        }
        return true;
    }
}
